import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 80,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

function generateCroppedImageFile(image, crop, fileName) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelCrop = convertToPixelCrop(crop, image.width, image.height);
  const outputSize = 200;

  canvas.width = outputSize;
  canvas.height = outputSize;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas is not supported");
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create cropped image"));
          return;
        }

        const baseName = (fileName || "profile-picture").replace(/\.[^/.]+$/, "");
        resolve(
          new File([blob], `${baseName}-profile.webp`, {
            type: "image/webp",
            lastModified: Date.now(),
          })
        );
      },
      "image/webp",
      0.92
    );
  });
}

export default function ProfileImageCropDialog({
  open,
  image,
  onClose,
  onConfirm,
  t,
}) {
  const imageRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [safeImageUrl, setSafeImageUrl] = useState("");

  useEffect(() => {
    let objectUrl = "";
    let isActive = true;

    const prepareSafeImage = async () => {
      if (!open || !image?.url) {
        setSafeImageUrl("");
        return;
      }

      try {
        const response = await fetch(image.url);
        if (!response.ok) {
          throw new Error("Failed to load image for cropping");
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (isActive) setSafeImageUrl(objectUrl);
      } catch (_error) {
        // Fallback to source URL if blob fetch fails.
        if (isActive) setSafeImageUrl(image.url);
      }
    };

    prepareSafeImage();

    return () => {
      isActive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, image?.url]);

  useEffect(() => {
    if (!open) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setIsSubmitting(false);
      setSafeImageUrl("");
    }
  }, [open]);

  const handleImageLoad = (event) => {
    const { width, height } = event.currentTarget;
    const centeredCrop = centerAspectCrop(width, height, 1);
    imageRef.current = event.currentTarget;
    setCrop(centeredCrop);
    setCompletedCrop(centeredCrop);
  };

  const handleConfirm = async () => {
    if (!imageRef.current || !completedCrop?.width || !completedCrop?.height) {
      return;
    }

    try {
      setIsSubmitting(true);
      const croppedFile = await generateCroppedImageFile(
        imageRef.current,
        completedCrop,
        image?.key?.split("/").pop() || image?.url?.split("/").pop()
      );
      await onConfirm(croppedFile);
    } catch (error) {
      console.error("Image crop failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("makeProfilePicture")}</DialogTitle>
      <DialogContent>
        {/* <Typography variant="body2" sx={{ mb: 2 }}>
          {t("dragAndDropImage") || "Adjust the crop for your profile picture."}
        </Typography> */}
        <Box sx={{ display: "flex", justifyContent: "center", overflow: "hidden" }}>
          {safeImageUrl ? (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(pixelCrop, percentCrop) => {
                setCompletedCrop(percentCrop);
              }}
              aspect={1}
              keepSelection
              minWidth={120}
            >
              <img
                src={safeImageUrl}
                alt={image.url}
                crossOrigin="anonymous"
                onLoad={handleImageLoad}
                style={{ maxHeight: "70vh", width: "100%", objectFit: "contain" }}
              />
            </ReactCrop>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting} color="inherit">
          {t("close") || "Close"}
        </Button>
        <Button onClick={handleConfirm} disabled={isSubmitting || !completedCrop} variant="contained">
          {isSubmitting ? "Saving..." : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
